import { ReactNode } from "react";

import { HasErrors } from "@/components/types";
import { Label } from "@/components/ui/Label";
import { cn } from "@/lib/cn";

import { Combobox } from "./Combobox";
import { SelectOption, SelectOptions } from "./types";

export type MultiSelectProps<OptionValue extends string> = HasErrors & {
  id: string;
  /** Focus the control when it is mounted */
  autoFocus?: boolean;
  /** Sets a className attribute on the component wrapper */
  className?: string;
  /**
   * False keeps the chosen options out of the control, which then shows the placeholder: for a filter
   * whose choices are listed as chips elsewhere and whose placeholder summarizes them
   */
  controlShouldRenderValue?: boolean;
  /** Is the select value clearable. Shows clear indicator when true */
  isClearable?: boolean;
  /** Is the select disabled */
  isDisabled?: boolean;
  /** Is the select in loading state (Async) */
  isLoading?: boolean;
  /** Label copy for the select (a11y) */
  label: string;
  /** Should the label be visible in the UI */
  labelVisible?: boolean;
  /** Element to show when no options visible in dropdown */
  noOptionsMessage?: (obj: { inputValue: string }) => ReactNode;
  /** Handle change events on the select */
  onChange: (options: readonly SelectOption<OptionValue>[]) => void;
  /** Options that populate the select menu, flat or in labelled groups */
  options: SelectOptions<OptionValue>;
  /** Custom placeholder copy */
  placeholder?: string;
  /** Is the select required. Renders an asterisk near label if set to true */
  required?: boolean;
  value?: readonly SelectOption<OptionValue>[];
};

export function MultiSelect<OptionValue extends string>(props: MultiSelectProps<OptionValue>) {
  const {
    id,
    className,
    label,
    labelVisible,
    noOptionsMessage = () => "Nothing to select...",
    placeholder = "Select items...",
    required,
    value = [],
    ...other
  } = props;

  return (
    <div
      className={cn("relative min-w-[200px] text-sm font-semibold", className)}
      data-testid="multi-select-wrapper"
    >
      <Label htmlFor={id} variant="field" className={cn(labelVisible === false && "sr-only")}>
        {label}
        {required && " *"}
      </Label>
      <Combobox
        {...other}
        isMulti
        id={id}
        label={label}
        placeholder={placeholder}
        noOptionsMessage={noOptionsMessage}
        value={value}
      />
    </div>
  );
}
